import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LungScreeningTest = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    date_of_birth: '',
    years_smoking: '',
    cigarettes_per_day: '',
    has_quit: null,
    years_since_quit: ''
  });

  const questions = [
    {
      id: 'date_of_birth',
      title_fr: 'Quelle est votre date de naissance ?',
      title_en: 'What is your date of birth?',
      type: 'date'
    },
    {
      id: 'years_smoking',
      title_fr: 'Vous avez fumé pendant combien d\'années ?',
      title_en: 'How many years have you been smoking?',
      type: 'number',
      placeholder: '20'
    },
    {
      id: 'cigarettes_per_day',
      title_fr: 'Combien de cigarettes environ fumez-vous par jour ?',
      title_en: 'Approximately how many cigarettes do you smoke per day?',
      type: 'number',
      placeholder: '15'
    },
    {
      id: 'has_quit',
      title_fr: 'Est-ce que vous avez arrêté de fumer ?',
      title_en: 'Have you quit smoking?',
      type: 'boolean'
    },
    {
      id: 'years_since_quit',
      title_fr: 'Si oui, depuis combien d\'années ?',
      title_en: 'If yes, how many years ago?',
      type: 'number',
      placeholder: '5',
      conditional: true
    }
  ];

  // Filter questions based on answers
  const activeQuestions = questions.filter(q => {
    if (q.id === 'years_since_quit') {
      return formData.has_quit === true;
    }
    return true;
  });

  const currentQuestion = activeQuestions[currentStep];
  const progress = ((currentStep + 1) / activeQuestions.length) * 100;
  const isLastStep = currentStep === activeQuestions.length - 1;

  const validateCurrentStep = () => {
    const value = formData[currentQuestion.id];
    
    if (currentQuestion.type === 'boolean' && value === null) {
      toast.error(language === 'fr' ? 'Veuillez répondre à la question' : 'Please answer the question');
      return false;
    }
    
    if (currentQuestion.type === 'date' && !value) {
      toast.error(language === 'fr' ? 'Veuillez entrer votre date de naissance' : 'Please enter your date of birth');
      return false;
    }
    
    if (currentQuestion.type === 'number' && (!value || value === '')) {
      toast.error(language === 'fr' ? 'Veuillez entrer une valeur' : 'Please enter a value');
      return false;
    }
    
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;

    if (isLastStep) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/lung-screening`, {
        date_of_birth: formData.date_of_birth,
        years_smoking: parseInt(formData.years_smoking),
        cigarettes_per_day: parseInt(formData.cigarettes_per_day),
        has_quit: formData.has_quit,
        years_since_quit: formData.has_quit ? parseInt(formData.years_since_quit) : 0
      });

      navigate('/lung-screening-results', { state: { result: response.data } });
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la soumission' : 'Error submitting test');
    } finally {
      setLoading(false);
    }
  };

  const renderQuestionContent = () => {
    switch (currentQuestion.type) {
      case 'date':
        return (
          <div className="space-y-4">
            <Input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => setFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
              className="h-14 text-lg rounded-xl border-gray-200 bg-white/50 focus:ring-2 focus:ring-red-200"
              data-testid="dob-input"
            />
          </div>
        );
        
      case 'number':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="w-32 h-32 rounded-full bg-red-50 flex items-center justify-center">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData[currentQuestion.id]}
                  onChange={(e) => setFormData(prev => ({ ...prev, [currentQuestion.id]: e.target.value }))}
                  placeholder={currentQuestion.placeholder}
                  className="w-20 h-20 text-center text-4xl font-bold border-0 bg-transparent focus:ring-0"
                  data-testid={`${currentQuestion.id}-input`}
                />
              </div>
            </div>
            <p className="text-center text-gray-500">
              {language === 'fr' ? 'années' : 'years'}
            </p>
          </div>
        );
        
      case 'boolean':
        return (
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => setFormData(prev => ({ ...prev, has_quit: true }))}
              className={`flex-1 max-w-[150px] p-6 rounded-2xl border-2 transition-all ${
                formData.has_quit === true 
                  ? 'border-red-500 bg-red-50 shadow-lg' 
                  : 'border-gray-200 hover:border-red-300'
              }`}
              data-testid="option-yes"
            >
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${
                  formData.has_quit === true ? 'bg-red-500 text-white' : 'bg-gray-100'
                }`}>
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <span className="font-semibold text-lg">
                  {language === 'fr' ? 'Oui' : 'Yes'}
                </span>
              </div>
            </button>
            
            <button
              onClick={() => setFormData(prev => ({ ...prev, has_quit: false, years_since_quit: '0' }))}
              className={`flex-1 max-w-[150px] p-6 rounded-2xl border-2 transition-all ${
                formData.has_quit === false 
                  ? 'border-red-500 bg-red-50 shadow-lg' 
                  : 'border-gray-200 hover:border-red-300'
              }`}
              data-testid="option-no"
            >
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${
                  formData.has_quit === false ? 'bg-red-500 text-white' : 'bg-gray-100'
                }`}>
                  <span className="text-2xl">✕</span>
                </div>
                <span className="font-semibold text-lg">
                  {language === 'fr' ? 'Non' : 'No'}
                </span>
              </div>
            </button>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="lung-screening">
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8 animate-fade-in-up">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              {language === 'fr' ? 'Question' : 'Question'} {currentStep + 1} {language === 'fr' ? 'sur' : 'of'} {activeQuestions.length}
            </span>
            <span className="text-sm font-medium text-red-600">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-2 bg-gray-200" />
        </div>

        {/* Question Card */}
        <div className="glass-card rounded-3xl p-8 sm:p-10 shadow-xl animate-fade-in-up" data-testid="screening-card">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full mb-6">
            <Stethoscope className="w-4 h-4 text-red-600" />
            <span className="text-sm font-semibold text-red-600">
              {language === 'fr' ? 'Dépistage Cancer Pulmonaire' : 'Lung Cancer Screening'}
            </span>
          </div>

          {/* Question Text */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-8 leading-relaxed">
            {language === 'fr' ? currentQuestion.title_fr : currentQuestion.title_en}
          </h2>

          {/* Question Content */}
          <div className="mb-8">
            {renderQuestionContent()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-100">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="rounded-full"
              data-testid="prev-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Précédent' : 'Previous'}
            </Button>

            <Button
              onClick={handleNext}
              disabled={loading}
              className="rounded-full bg-red-600 hover:bg-red-700 shadow-lg px-8"
              data-testid="next-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isLastStep ? (
                <>
                  {language === 'fr' ? 'Voir le résultat' : 'See result'}
                  <CheckCircle2 className="ml-2 w-4 h-4" />
                </>
              ) : (
                <>
                  {language === 'fr' ? 'Suivant' : 'Next'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Important Notice */}
        <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-2xl animate-fade-in-up">
          <p className="text-sm text-red-700 text-center">
            {language === 'fr' 
              ? 'Ce test est obligatoire pour tous les nouveaux utilisateurs afin d\'évaluer votre éligibilité au dépistage du cancer pulmonaire.'
              : 'This test is mandatory for all new users to assess your eligibility for lung cancer screening.'}
          </p>
        </div>
      </div>
    </div>
  );
};
